import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link', 'image'],
    ['clean']
  ],
};

const RakutenEventWorkspace = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageUuid = searchParams.get('uuid');
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [mains, setMains] = useState([]);
  const [selectedMain, setSelectedMain] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [previewHtml, setPreviewHtml] = useState('');

  const [mainForm, setMainForm] = useState({
    name: '', html_code: '', row_col: '', anker: '', visible_check: true,
    bg_color: '#ffffff', padding_y: '20px'
  });

  const [showItemModal, setShowItemModal] = useState(false);
  const [itemCrudMode, setItemCrudMode] = useState('insert');
  const [selectedItemUuid, setSelectedItemUuid] = useState('');

  // 🌟 商品フォームの状態（タイマー開始・終了時間を追加）
  const [itemForm, setItemForm] = useState({
    name: '', img_url: '', item_url: '', html_code: '', visible_check: true,
    item_color: true, item_title: true, item_price: true, item_favorite: true,
    timer_start: '', timer_end: '' // 👈 カレンダー入力用
  });

  const [dragMainIndex, setDragMainIndex] = useState(null);
  const [dragItemIndex, setDragItemIndex] = useState(null);

  const fetchMains = async () => {
    if (!pageUuid) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/rakuten-event/mains/by-page/${pageUuid}`);
      const data = await res.json();
      const safeData = Array.isArray(data) ? data : [];
      setMains(safeData);

      if (safeData.length > 0 && !selectedMain) {
        handleSelectMain(safeData[0]);
      } else if (safeData.length === 0) {
        setSelectedMain(null);
      }
    } catch (error) {
      console.error("大ブロック取得エラー:", error);
      setMains([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async (mainUuid) => {
    try {
      const res = await fetch(`${API_URL}/rakuten-event/items/by-main/${mainUuid}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("中ブロック取得エラー:", error);
      setItems([]);
    }
  };

  // 🌟 【進化】大ブロックだけでなく、編集中の「商品（アイテム）」の仮状態も一緒にバックエンドに送る
  const refreshTemporaryPreview = async (currentMainForm, currentItemForm = null) => {
    if (!selectedMain || !pageUuid) return;
    try {
      const generatedHtml = `<div style="background-color: ${currentMainForm.bg_color}; padding-top: ${currentMainForm.padding_y}; padding-bottom: ${currentMainForm.padding_y};">\n  <div class="container container-${selectedMain.uuid}">\n    \n  </div>\n</div>`;

      // 商品データのペイロードを作成
      let itemPayload = null;
      if (currentItemForm) {
        itemPayload = {
          uuid: selectedItemUuid || "new_temporary_item",
          name: currentItemForm.name,
          html_code: currentItemForm.html_code,
          img_url: currentItemForm.img_url,
          item_url: currentItemForm.item_url,
          item_color: currentItemForm.item_color,
          item_title: currentItemForm.item_title,
          item_price: currentItemForm.item_price,
          item_favorite: currentItemForm.item_favorite,
          // レガシー形式の [開始, 終了] 配列に変換して送信
          event_timer: [currentItemForm.timer_start, currentItemForm.timer_end]
        };
      }

      const res = await fetch(`${API_URL}/rakuten-event/html/preview-temporary/${pageUuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_main: {
            uuid: selectedMain.uuid,
            html_code: generatedHtml,
            row_col: currentMainForm.row_col,
            anker: currentMainForm.anker
          },
          current_item: itemPayload // 👈 商品の仮情報も一緒にパス！
        })
      });
      if (res.ok) {
        const html = await res.text();
        setPreviewHtml(html);
      }
    } catch (error) {
      console.error("仮プレビュー生成エラー:", error);
    }
  };

  useEffect(() => {
    if (!pageUuid) {
      alert("ファイルが指定されていません。");
      navigate('/rakuten-event/pages');
      return;
    }
    fetchMains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageUuid]);

  useEffect(() => {
    if (selectedMain) {
      refreshTemporaryPreview(mainForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMain, items]);

  const handleSelectMain = (main) => {
    setSelectedMain(main);

    const hCode = main.html_code || '';
    const bgMatch = hCode.match(/background-color:\s*(#[0-9a-fA-F]{6}|rgba?\(.*?\))/);
    const padMatch = hCode.match(/padding-top:\s*([0-9]+px)/);

    const nextForm = {
      name: main.name || '',
      html_code: hCode,
      row_col: main.row_col || '',
      anker: main.anker || '',
      visible_check: main.visible_check,
      bg_color: bgMatch ? bgMatch[1] : '#ffffff',
      padding_y: padMatch ? padMatch[1] : '20px'
    };

    setMainForm(nextForm);
    fetchItems(main.uuid);
  };

  const handleOpenPreviewWindow = () => {
    const blob = new Blob([previewHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, 'previewWindow', 'width=1000,height=800,scrollbars=yes,resizable=yes');
  };

  const handleFieldChange = (updatedFields) => {
    setMainForm(prev => {
      const nextForm = { ...prev, ...updatedFields };
      const generatedHtml = `<div style="background-color: ${nextForm.bg_color}; padding-top: ${nextForm.padding_y}; padding-bottom: ${nextForm.padding_y};">\n  <div class="container container-${selectedMain?.uuid}">\n    \n  </div>\n</div>`;
      nextForm.html_code = generatedHtml;
      refreshTemporaryPreview(nextForm);
      return nextForm;
    });
  };

  // 🌟 商品のモーダル内で文字やタイマーをいじった時にプレビューを同期させる関数
  const handleItemFieldChange = (updatedFields) => {
    setItemForm(prev => {
      const nextItemForm = { ...prev, ...updatedFields };
      // 大ブロックの状態と、新しい商品の状態を同時に仮プレビューに送る！
      refreshTemporaryPreview(mainForm, nextItemForm);
      return nextItemForm;
    });
  };

  // ==========================
  // 大ブロック (Main) の操作
  // ==========================
  const handleCreateMain = async () => {
    const newName = prompt("新しい大ブロックの名前を入力してください");
    if (!newName) return;
    try {
      const res = await fetch(`${API_URL}/rakuten-event/mains/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name_uuid: pageUuid, name: newName, visible_check: true })
      });
      if (res.ok) {
        const newMain = await res.json();
        await fetchMains();
        handleSelectMain(newMain);
      }
    } catch (error) { console.error("作成エラー:", error); }
  };

  const handleUpdateMain = async (e) => {
    e.preventDefault();
    if (!selectedMain) return;
    try {
      const res = await fetch(`${API_URL}/rakuten-event/mains/${selectedMain.uuid}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(mainForm)
      });
      if (res.ok) {
        fetchMains();
        alert("大ブロックの設定をデータベースに保存しました！");
      }
    } catch (error) { console.error("更新エラー:", error); }
  };

  const handleDeleteMain = async () => {
    if (!window.confirm(`「${selectedMain.name}」を削除しますか？`)) return;
    try {
      const res = await fetch(`${API_URL}/rakuten-event/mains/${selectedMain.uuid}`, { method: 'DELETE' });
      if (res.ok) {
        setSelectedMain(null);
        fetchMains();
      }
    } catch (error) { console.error("削除エラー:", error); }
  };

  const handleDragMainEnter = (index) => {
    if (dragMainIndex === index) return;
    const newMains = [...mains];
    const dragged = newMains[dragMainIndex];
    newMains.splice(dragMainIndex, 1);
    newMains.splice(index, 0, dragged);
    setDragMainIndex(index);
    setMains(newMains);
  };

  const handleDragMainEnd = async () => {
    setDragMainIndex(null);
    const sortData = mains.map((m, i) => ({ uuid: m.uuid, serial_num: i + 1 }));
    await fetch(`${API_URL}/rakuten-event/mains/sort`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sortData)
    });
    if (selectedMain) refreshTemporaryPreview(mainForm);
  };

  // ==========================
  // 中ブロック (Item) の操作
  // ==========================
  const openCreateItemModal = () => {
    setItemCrudMode('insert');
    setSelectedItemUuid('');
    const initForm = {
      name: '', img_url: '', item_url: '', html_code: '', visible_check: true,
      item_color: true, item_title: true, item_price: true, item_favorite: true,
      timer_start: '', timer_end: ''
    };
    setItemForm(initForm);
    setShowItemModal(true);
    // モーダルを開いた瞬間もプレビューに仮追加して見せる
    refreshTemporaryPreview(mainForm, initForm);
  };

  const openEditItemModal = (item) => {
    setItemCrudMode('update');
    setSelectedItemUuid(item.uuid);

    // タイマーの配列 [開始, 終了] があれば復元
    const tStart = item.event_timer && item.event_timer[0] ? item.event_timer[0] : '';
    const tEnd = item.event_timer && item.event_timer[1] ? item.event_timer[1] : '';

    const editForm = {
      name: item.name || '', img_url: item.img_url || '', item_url: item.item_url || '',
      html_code: item.html_code || '',
      visible_check: item.visible_check, item_color: item.item_color,
      item_title: item.item_title, item_price: item.item_price, item_favorite: item.item_favorite,
      timer_start: tStart, timer_end: tEnd
    };
    setItemForm(editForm);
    setShowItemModal(true);
    refreshTemporaryPreview(mainForm, editForm);
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    const url = itemCrudMode === 'insert' ? `${API_URL}/rakuten-event/items/` : `${API_URL}/rakuten-event/items/${selectedItemUuid}`;
    const method = itemCrudMode === 'insert' ? 'POST' : 'PUT';

    // 送信データを作る際に、カレンダーの文字を [開始, 終了] 配列に戻してあげる
    const payload = {
      ...(itemCrudMode === 'insert' ? { ...itemForm, name_uuid: selectedMain.uuid } : itemForm),
      event_timer: [itemForm.timer_start, itemForm.timer_end]
    };

    try {
      const res = await fetch(url, {
        method: method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowItemModal(false);
        fetchItems(selectedMain.uuid);
      } else {
        alert("保存に失敗しました。");
      }
    } catch (error) { console.error("保存エラー:", error); }
  };

  const handleDeleteItem = async (uuid) => {
    if (!window.confirm("この商品を削除してよろしいですか？")) return;
    try {
      const res = await fetch(`${API_URL}/rakuten-event/items/${uuid}`, { method: 'DELETE' });
      if (res.ok) {
        fetchItems(selectedMain.uuid);
      }
    } catch (error) { console.error("削除エラー:", error); }
  };

  const handleDragItemEnter = (index) => {
    if (dragItemIndex === index) return;
    const newItems = [...items];
    const dragged = newItems[dragItemIndex];
    newItems.splice(dragItemIndex, 1);
    newItems.splice(index, 0, dragged);
    setDragItemIndex(index);
    setItems(newItems);
  };

  const handleDragItemEnd = async () => {
    setDragItemIndex(null);
    const sortData = items.map((item, i) => ({ uuid: item.uuid, serial_num: i + 1 }));
    await fetch(`${API_URL}/rakuten-event/items/sort`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sortData)
    });
    if (selectedMain) refreshTemporaryPreview(mainForm);
  };

  return (
    <div className="container-fluid px-4">
      <ol className="breadcrumb float-xl-end mt-3">
        <li className="breadcrumb-item"><Link to="/">Home</Link></li>
        <li className="breadcrumb-item"><Link to="/rakuten-event/pages">楽天イベントページ</Link></li>
        <li className="breadcrumb-item active">ワークスペース</li>
      </ol>
      <h1 className="page-header mt-3">イベント編集ワークスペース</h1>

      <div className="row">
        {/* === 左ペイン：大ブロック一覧 === */}
        <div className="col-md-3">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">大ブロック</h4>
              <div className="panel-heading-btn">
                <button className="btn btn-xs btn-primary" onClick={handleCreateMain}>
                  <i className="fa fa-plus me-1"></i>追加
                </button>
              </div>
            </div>
            <div className="list-group list-group-flush" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center p-4"><i className="fa fa-spinner fa-spin"></i></div>
              ) : (
                mains.map((main, index) => (
                  <div
                    key={main.uuid}
                    className={`list-group-item list-group-item-action d-flex align-items-center ${selectedMain?.uuid === main.uuid ? 'active' : ''}`}
                    style={{ cursor: 'pointer', padding: '10px' }}
                    onClick={() => handleSelectMain(main)}
                    draggable="true"
                    onDragStart={() => setDragMainIndex(index)}
                    onDragEnter={() => handleDragMainEnter(index)}
                    onDragEnd={handleDragMainEnd}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <i className="fa fa-bars text-muted me-2" style={{ cursor: 'grab' }}></i>
                    <div className="flex-fill text-truncate" style={{ fontSize: '13px' }}>{main.name || '名称未設定'}</div>
                    {!main.visible_check && <i className="fa fa-eye-slash text-warning ms-1"></i>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* === 中央ペイン：詳細編集画面 === */}
        <div className="col-md-5">
          {!selectedMain ? (
            <div className="panel panel-inverse">
              <div className="panel-body text-center p-5 text-muted">
                <h4>左側のリストから選択してください</h4>
              </div>
            </div>
          ) : (
            <>
              {/* 中央 上段: 大ブロック属性 */}
              <div className="panel panel-inverse">
                <div className="panel-heading">
                  <h4 className="panel-title">設定: {selectedMain.name}</h4>
                  <div className="panel-heading-btn">
                    <button className="btn btn-xs btn-danger" onClick={handleDeleteMain}>削除</button>
                  </div>
                </div>
                <div className="panel-body bg-light p-2">
                  <form onSubmit={handleUpdateMain}>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label mb-1" style={{ fontSize: '12px' }}>ブロック名</label>
                        <input type="text" className="form-control form-control-sm" value={mainForm.name} onChange={e => handleFieldChange({ name: e.target.value })} />
                      </div>
                      <div className="col-3">
                        <label className="form-label mb-1" style={{ fontSize: '12px' }}>行表示件数</label>
                        <input type="text" className="form-control form-control-sm" value={mainForm.row_col} onChange={e => handleFieldChange({ row_col: e.target.value })} placeholder="例: 3" />
                      </div>
                      <div className="col-3">
                        <label className="form-label mb-1" style={{ fontSize: '12px' }}>アンカー名</label>
                        <input type="text" className="form-control form-control-sm" value={mainForm.anker} onChange={e => handleFieldChange({ anker: e.target.value })} />
                      </div>

                      {/* 背景色 */}
                      <div className="col-6">
                        <label className="form-label mb-1" style={{ fontSize: '12px' }}>背景色</label>
                        <div className="d-flex align-items-center">
                          <input type="color" className="form-control form-control-sm p-0 border-0" style={{ width: '35px', height: '30px', cursor: 'pointer' }} value={mainForm.bg_color} onChange={e => handleFieldChange({ bg_color: e.target.value })} />
                          <span className="ms-2" style={{ fontSize: '12px', fontFamily: 'monospace' }}>{mainForm.bg_color}</span>
                        </div>
                      </div>

                      {/* 上下余白 */}
                      <div className="col-6">
                        <label className="form-label mb-1" style={{ fontSize: '12px' }}>上下余白</label>
                        <select className="form-select form-select-sm" value={mainForm.padding_y} onChange={e => handleFieldChange({ padding_y: e.target.value })}>
                          <option value="0px">なし (0px)</option>
                          <option value="10px">極小 (10px)</option>
                          <option value="20px">小 (20px)</option>
                          <option value="30px">中 (30px)</option>
                          <option value="40px">大 (40px)</option>
                          <option value="50px">特大 (50px)</option>
                        </select>
                      </div>

                      <div className="col-12 mt-2">
                        <label className="form-label mb-1" style={{ fontSize: '12px' }}>HTMLコード (保存ボタンで確定します)</label>
                        <textarea className="form-control form-control-sm" rows="3" value={mainForm.html_code} readOnly style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }} placeholder="色や余白を選ぶと自動生成されます"></textarea>
                      </div>

                      <div className="col-12 d-flex justify-content-between align-items-center mt-2">
                        <div className="form-check form-switch">
                          <input className="form-check-input" type="checkbox" id="visibleSwitch" checked={mainForm.visible_check} onChange={e => setMainForm({ ...mainForm, visible_check: e.target.checked })} />
                          <label className="form-check-label" htmlFor="visibleSwitch" style={{ fontSize: '12px' }}>表示する</label>
                        </div>
                        <button type="submit" className="btn btn-primary btn-sm"><i className="fa fa-save me-1"></i>設定を保存</button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* 中ブロック(商品)一覧 */}
              <div className="panel panel-default border">
                <div className="panel-heading bg-white border-bottom">
                  <h4 className="panel-title text-dark">登録されている商品</h4>
                  <div className="panel-heading-btn">
                    <button className="btn btn-xs btn-success" onClick={openCreateItemModal}>
                      <i className="fa fa-plus me-1"></i>商品追加
                    </button>
                  </div>
                </div>
                <div className="panel-body p-0" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                  <table className="table table-hover mb-0" style={{ fontSize: '12px' }}>
                    <thead className="table-light">
                      <tr>
                        <th width="1%">#</th>
                        <th width="50%">商品名</th>
                        <th width="10%" className="text-center">表示</th>
                        <th width="39%">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr
                          key={item.uuid}
                          draggable="true"
                          onDragStart={() => setDragItemIndex(index)}
                          onDragEnter={() => handleDragItemEnter(index)}
                          onDragEnd={handleDragItemEnd}
                          onDragOver={(e) => e.preventDefault()}
                        >
                          <td className="text-muted"><i className="fa fa-bars" style={{ cursor: 'grab' }}></i></td>
                          <td className="text-truncate" style={{ maxWidth: '150px' }}>{item.name || '-'}</td>
                          <td className="text-center">
                            {item.visible_check ? <i className="fa fa-check text-success"></i> : <i className="fa fa-times text-danger"></i>}
                          </td>
                          <td>
                            <button className="btn btn-xs btn-white me-1" onClick={() => openEditItemModal(item)}>編集</button>
                            <button className="btn btn-xs btn-danger" onClick={() => handleDeleteItem(item.uuid)}>削除</button>
                          </td>
                        </tr>
                      ))}
                      {items.length === 0 && (
                        <tr><td colSpan="4" className="text-center p-3 text-muted">まだ商品がありません</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* === 右ペイン：常時プレビュー === */}
        <div className="col-md-4">
          <div className="panel panel-inverse" style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div className="panel-heading">
              <h4 className="panel-title">プレビュー</h4>
              <div className="panel-heading-btn">
                <button
                  className="btn btn-xs btn-info me-1"
                  onClick={handleOpenPreviewWindow}
                  title="別ウィンドウでプレビューを開く"
                >
                  <i className="fa fa-external-link-alt"></i> 別窓
                </button>
              </div>
            </div>
            <div className="panel-body p-0 flex-grow-1" style={{ backgroundColor: '#fff' }}>
              <iframe
                srcDoc={previewHtml}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="HTML Preview"
              ></iframe>
            </div>
          </div>
        </div>

      </div>

      {/* === 中ブロック(商品)追加・編集モーダル === */}
      {showItemModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{itemCrudMode === 'insert' ? '商品の追加' : '商品の編集'}</h5>
                <button type="button" className="btn-close" onClick={() => { setShowItemModal(false); refreshTemporaryPreview(mainForm); }}></button>
              </div>
              <form onSubmit={handleSubmitItem}>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">商品名 / 識別用メモ</label>
                      <input type="text" className="form-control" value={itemForm.name} onChange={e => handleItemFieldChange({ name: e.target.value })} required />
                    </div>
                    <div className="col-md-12 mb-3">
                      <label className="form-label">商品URL (item_url)</label>
                      <input type="text" className="form-control" value={itemForm.item_url} onChange={e => handleItemFieldChange({ item_url: e.target.value })} />
                    </div>
                    <div className="col-md-12 mb-3">
                      <label className="form-label">画像URL (img_url)</label>
                      <input type="text" className="form-control" value={itemForm.img_url} onChange={e => handleItemFieldChange({ img_url: e.target.value })} />
                    </div>

                    {/* ⏰ レガシーから完全移植：カレンダー付きタイマー設定欄 */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label text-danger fw-bold"><i className="fa fa-clock me-1"></i>タイマー開始時間</label>
                      <input type="datetime-local" className="form-control" value={itemForm.timer_start} onChange={e => handleItemFieldChange({ timer_start: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label text-danger fw-bold"><i className="fa fa-clock me-1"></i>タイマー終了時間</label>
                      <input type="datetime-local" className="form-control" value={itemForm.timer_end} onChange={e => handleItemFieldChange({ timer_end: e.target.value })} />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label text-primary">HTMLエディター (入力すると自動プレビュー)</label>
                      <div style={{ backgroundColor: '#fff', paddingBottom: '40px' }}>
                        <ReactQuill
                          theme="snow"
                          modules={quillModules}
                          value={itemForm.html_code}
                          onChange={(content) => handleItemFieldChange({ html_code: content })}
                          style={{ height: '150px' }}
                        />
                      </div>
                    </div>

                  </div>
                  <hr />
                  <div className="row">
                    <div className="col-md-3">
                      <div className="form-check"><input className="form-check-input" type="checkbox" checked={itemForm.item_title} onChange={e => handleItemFieldChange({ item_title: e.target.checked })} /><label className="form-check-label">タイトル表示</label></div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-check"><input className="form-check-input" type="checkbox" checked={itemForm.item_price} onChange={e => handleItemFieldChange({ item_price: e.target.checked })} /><label className="form-check-label">価格表示</label></div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-check"><input className="form-check-input" type="checkbox" checked={itemForm.item_color} onChange={e => handleItemFieldChange({ item_color: e.target.checked })} /><label className="form-check-label">カラーパレット</label></div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-check"><input className="form-check-input" type="checkbox" checked={itemForm.item_favorite} onChange={e => handleItemFieldChange({ item_favorite: e.target.checked })} /><label className="form-check-label">お気に入りボタン</label></div>
                    </div>
                  </div>
                  <div className="mt-4 form-check form-switch text-end">
                    <input className="form-check-input" type="checkbox" checked={itemForm.visible_check} onChange={e => handleItemFieldChange({ visible_check: e.target.checked })} />
                    <label className="form-check-label">この商品を表示する</label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowItemModal(false); refreshTemporaryPreview(mainForm); }}>閉じる</button>
                  <button type="submit" className="btn btn-primary">保存</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RakutenEventWorkspace;